import { type ReportDetail, type ReportResolution } from '@aysar/contracts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ModalData } from '@/shared/store/reducers/modal/modal.types';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import { useReportQuery, useResolveReportMutation } from '../../services/moderation/moderation.api';
import { moderationErrorCopy } from '../../utils/moderation-error/moderation-error.util';

export interface UseResolveReportModalLogic {
  /** `null` when the payload carried no usable id — the dialog then draws nothing. */
  reportId: string | null;
  report: ReportDetail | null;
  isLoading: boolean;
  /** The report itself could not be read. Distinct from a refused verdict. */
  loadError: ApiError | null;
  reload: () => void;
  /** Somebody has already decided it — the dialog shows the verdict, read-only. */
  isDecided: boolean;
  /** The operator may record a verdict on THIS report, right now. */
  canResolve: boolean;
  resolution: ReportResolution | null;
  setResolution: (value: ReportResolution) => void;
  note: string;
  setNote: (value: string) => void;
  canSubmit: boolean;
  isSaving: boolean;
  /** The api's refusal, already in this module's words. */
  error: string | null;
  /** A colleague decided first: the answer is re-read, never retry. */
  isConflict: boolean;
  /** The report is not there any more: there is nothing to go back and read. */
  isGone: boolean;
  submit: () => Promise<void>;
}

/**
 * Reading one report, and deciding it.
 *
 * ## It reads its own copy, and that is the point
 *
 * The payload is the report **id** alone — `ModalData` is serializable-only, and
 * a dialog that carried a snapshot of the row would be deciding a report the
 * queue's 30-second poll may already have moved on from. Reading its own entry
 * also gets the reporter's words in full: 2000 characters do not survive a table
 * cell, and nobody should record a verdict on a complaint they have seen the
 * first line of.
 *
 * ## Reading and deciding are one dialog, gated at the CONTROL
 *
 * `moderation.review` opens it; `moderation.manage` decides it. Splitting them
 * into two dialogs would mean a reviewer who may only read has nowhere to read —
 * and hiding the whole thing from them would hide the queue's only legible
 * surface. So the gate sits on the verdict, which is the permission the api will
 * actually check (P10.6j).
 *
 * ## A conflict is not a failure
 *
 * The queue polls, so two reviewers landing on one row is the ordinary case, and
 * the colleague who got there first is **right**. `isConflict` is what lets the
 * dialog offer a re-read instead of a retry the api would refuse again — and the
 * dialog deliberately stays open, because a toast would take the news away
 * before it was read.
 */
export function useResolveReportModalLogic(
  modalData: ModalData | undefined,
  onClose: () => void,
): UseResolveReportModalLogic {
  const { t } = useTranslation('moderation');
  const toast = useToast();
  const { can } = useHasPermission();
  const [resolution, setResolution] = useState<ReportResolution | null>(null);
  const [note, setNote] = useState('');
  const [refusal, setRefusal] = useState<ApiError | null>(null);

  const reportId = readReportId(modalData);
  const query = useReportQuery(reportId ?? '', { skip: reportId === null });
  const [resolveReport, { isLoading: isSaving }] = useResolveReportMutation();

  const report = query.data ?? null;
  const isDecided = report !== null && report.status !== 'pending_review';
  const canResolve = report !== null && !isDecided && can('moderation.manage');

  const submit = useCallback(async () => {
    if (reportId === null || resolution === null) return;

    setRefusal(null);
    const trimmed = note.trim();

    try {
      await resolveReport({
        id: reportId,
        resolution,
        // `null`, never `''`. `ReportResolveSchema` trims and nullables the note,
        // so an empty string is a value on a field left alone on purpose.
        note: trimmed === '' ? null : trimmed,
      }).unwrap();

      toast.success(t('resolve.success'));
      onClose();
    } catch (caught) {
      setRefusal(toApiError(caught));
    }
  }, [note, onClose, reportId, resolution, resolveReport, t, toast]);

  const copy = moderationErrorCopy(refusal);

  return {
    reportId,
    report,
    isLoading: query.isLoading,
    loadError: query.error === undefined ? null : toApiError(query.error),
    reload: () => {
      void query.refetch();
    },
    isDecided,
    canResolve,
    resolution,
    setResolution,
    note,
    setNote,
    canSubmit: canResolve && resolution !== null && !isSaving,
    isSaving,
    error: refusal === null ? null : t(copy === null ? 'error.generic' : copy.i18nKey),
    isConflict: copy?.isConflict ?? false,
    isGone: copy?.isGone ?? false,
    submit,
  };
}

/**
 * Narrows the modal payload.
 *
 * `ModalData` is `Record<string, unknown>` by design — it survives a reload and a
 * serialization boundary — so the field is checked rather than cast. A dialog
 * that trusted the payload would decide whatever id happened to be in it.
 */
function readReportId(data: ModalData | undefined): string | null {
  if (data === undefined) return null;

  const value = data['reportId'];

  return typeof value === 'string' && value !== '' ? value : null;
}
